import {
  OrderType,
  QuestionGroupType,
  type SearchQuestionsQuery,
  useSearchQuestionsQuery,
} from '@exam-management/graphql';
import { Flex, PaperSection, Text } from '@shared/components';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { NoQuestionInLocationOverlay, QuestionInLocationList } from '../../components';
import type { LocationSectionType } from '../location-section';

const PAGE_SIZE = 10;

function updateQuerySearchQuestions(
  previousResult: SearchQuestionsQuery,
  {
    fetchMoreResult,
  }: {
    fetchMoreResult: SearchQuestionsQuery;
    variables: {
      paging: {
        pageSize: number;
        currentPage: number;
      };
    };
  },
) {
  if (!fetchMoreResult) return previousResult;

  const uniqueQuestions = [
    ...previousResult.qctSearchQuestions.questions,
    ...fetchMoreResult.qctSearchQuestions.questions,
  ].filter((question, index, self) => index === self.findIndex((q) => q.id === question.id));

  return {
    ...previousResult,
    qctSearchQuestions: {
      ...previousResult.qctSearchQuestions,
      questions: Array.from(uniqueQuestions),
    },
  };
}

interface SingleQuestionsInLocationSectionProps {
  questionId?: string;
  refQBankStructureId?: string;
}

export default function SingleQuestionsInLocationSection(
  props: Readonly<SingleQuestionsInLocationSectionProps>,
) {
  const { questionId, refQBankStructureId } = props;
  const { watch } = useFormContext<LocationSectionType>();

  const subTopicLevelItemId = watch('location')?.SUB_TOPIC?.value as string;
  const loq = watch('location.LOQ');
  const loqName = loq?.value?.label;
  const isLOQ = loq?.selected === 'true';
  const isUnableToView = !subTopicLevelItemId || (isLOQ && !loqName);

  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [subTopicLevelItemId]);

  const {
    data: searchQuestionsResult,
    loading: isSearchQuestionsLoading,
    fetchMore: fetchMoreQuestions,
  } = useSearchQuestionsQuery({
    variables: {
      search: {
        levelItemId: subTopicLevelItemId,
        questionGroupType:
          isLOQ && loqName ? [QuestionGroupType.LotOfQuestion] : [QuestionGroupType.Single],
        questionGroupName: isLOQ && loqName ? loqName : undefined,
        refQBankStructureId,
      },
      paging: {
        pageSize: PAGE_SIZE,
        currentPage: 0,
      },
      order: {
        field: 'createdAt',
        type: OrderType.Asc,
      },
    },
    skip: subTopicLevelItemId === undefined,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const handleFetchMore = async () => {
    await fetchMoreQuestions({
      variables: {
        paging: {
          pageSize: PAGE_SIZE,
          currentPage: currentPage + 1,
        },
      },
      updateQuery: updateQuerySearchQuestions,
    });
    setCurrentPage((prev) => prev + 1);
  };

  const questions = searchQuestionsResult?.qctSearchQuestions.questions ?? [];
  const totalQuestions = searchQuestionsResult?.qctSearchQuestions.total ?? 0;

  return (
    <PaperSection title='Q in this Location'>
      <Flex
        sx={(theme) => ({
          p: theme.spacing(4, 6, 6),
          flexDirection: 'column',
          gap: theme.spacing(2),
        })}
      >
        <Flex sx={{ justifyContent: 'space-between' }}>
          <Text variant='body2Semibold'>QID</Text>
          <Text variant='body2Semibold'>{totalQuestions} Qty.</Text>
        </Flex>
        {isUnableToView ? (
          <NoQuestionInLocationOverlay variant='no-location-selected' />
        ) : (
          <QuestionInLocationList
            questions={questions}
            loading={isSearchQuestionsLoading}
            hasMore={questions.length < totalQuestions}
            handleFetchMore={handleFetchMore}
            currentQuestionId={questionId}
            currentPage={currentPage}
          />
        )}
      </Flex>
    </PaperSection>
  );
}
